import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Wallet, ArrowUpRight, ArrowDownRight, History, CreditCard, 
  Plus, RefreshCw, TrendingUp, Shield, AlertCircle,
  ArrowLeft, Home, Search, Bell, User, Filter, Download,
  Calendar, X, ChevronLeft, ChevronRight, Smartphone, Monitor,
  Clock, FileText, CheckCircle, XCircle, Loader, Copy, ExternalLink,
  Receipt, Banknote, Tag, Building, Globe, Smartphone as SmartphoneIcon,
  CreditCard as CreditCardIcon, Wallet as WalletIcon, Check,
  Key, Shield as ShieldIcon, AlertTriangle, Info
} from 'lucide-react';
import Loading from '../../components/common/Loading';
import { getTransactionById } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const TransactionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const colors = {
    primary: '#ff660e',
    black: '#0f0e0e',
    white: '#ffffff',
    lightBg: '#f8f9fa'
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTransactionDetails();
  }, [id, user, navigate]);

  const fetchTransactionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getTransactionById(id, user.user_id);
      
      if (response && response.success) {
        // Handle the response structure from your API
        const transactionData = response.data || response.transaction;
        
        // Parse metadata if it's a string
        if (transactionData.metadata && typeof transactionData.metadata === 'string') {
          try {
            transactionData.metadata = JSON.parse(transactionData.metadata);
          } catch (e) {
            // console.error('Error parsing metadata:', e);
          }
        }
        
        // Parse fee_breakdown if it's a string
        if (transactionData.fee_breakdown && typeof transactionData.fee_breakdown === 'string') {
          try {
            transactionData.fee_breakdown = JSON.parse(transactionData.fee_breakdown);
          } catch (e) {
            // console.error('Error parsing fee breakdown:', e);
          }
        }
        
        setTransaction(transactionData);
      } else {
        setError('Transaction not found or access denied');
      }
    } catch (error) {
      // console.error('Error fetching transaction details:', error);
      setError('Failed to load transaction details. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTransactionDetails();
  };

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString('en-NG', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        time: date.toLocaleTimeString('en-NG', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      };
    } catch (error) {
      return { date: 'Invalid date', time: '' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'successful':
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'pending':
      case 'processing':
        return <Loader className="w-6 h-6 text-yellow-500 animate-spin" />;
      case 'failed':
      case 'declined':
      case 'abandoned':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <AlertCircle className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'successful':
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
      case 'processing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed':
      case 'declined':
      case 'abandoned':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusDescription = (status, metadata, description) => {
    if (status === 'failed') {
      // Extract failure reason from description
      if (description.includes('OTP expired')) {
        return 'Withdrawal failed: OTP verification expired';
      } else if (description.includes('OTP required')) {
        return 'Withdrawal failed: OTP verification required';
      }
      return 'Transaction failed to complete';
    } else if (status === 'pending') {
      if (metadata?.otp_required) {
        return 'Pending OTP verification';
      }
      return 'Transaction is being processed';
    } else if (status === 'successful') {
      return 'Transaction completed successfully';
    }
    return 'Transaction status unknown';
  };

  const getTransactionIcon = (type, transactionType) => {
    if (type === 'credit') {
      if (transactionType === 'deposit') {
        return <ArrowDownRight className="w-8 h-8 text-green-500" />;
      }
      return <ArrowDownRight className="w-8 h-8 text-blue-500" />;
    } else if (type === 'debit') {
      return <ArrowUpRight className="w-8 h-8 text-red-500" />;
    }
    return <CreditCard className="w-8 h-8 text-gray-500" />;
  };

  const getTransactionTypeLabel = (type, transactionType) => {
    if (type === 'credit' && transactionType === 'deposit') {
      return 'Deposit';
    } else if (type === 'credit' && !transactionType) {
      return 'Credit';
    } else if (type === 'debit' && transactionType === 'withdrawal') {
      return 'Withdrawal';
    } else if (type === 'debit') {
      return 'Debit';
    }
    return transactionType || type;
  };

  const getPaymentMethodIcon = (method) => {
    if (!method) return <CreditCard className="w-5 h-5" />;
    
    const methodLower = method.toLowerCase();
    if (methodLower.includes('opay') || methodLower.includes('bank')) {
      return <Building className="w-5 h-5" />;
    } else if (methodLower.includes('card')) {
      return <CreditCardIcon className="w-5 h-5" />;
    } else if (methodLower.includes('wallet')) {
      return <WalletIcon className="w-5 h-5" />;
    } else if (methodLower.includes('mobile')) {
      return <SmartphoneIcon className="w-5 h-5" />;
    }
    return <CreditCard className="w-5 h-5" />;
  };

  const parseBankDetails = (transaction) => {
    if (transaction.bank_account && typeof transaction.bank_account === 'object') {
      return transaction.bank_account;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  const bankDetails = parseBankDetails(transaction);
  const metadata = transaction?.metadata || {};
  const feeBreakdown = transaction?.fee_breakdown || {};

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/wallet/transactions')}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Transaction Details</h1>
            
            <button
              onClick={handleRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {transaction && (
          <>
            {/* Transaction Header Card */}
            <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-gray-100">
                    {getTransactionIcon(transaction.type, transaction.transaction_type)}
                  </div>
                  <div>
                    <h2 className="font-bold text-black">
                      {getTransactionTypeLabel(transaction.type, transaction.transaction_type)}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {transaction.description || 'No description'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(transaction.status)}
                  <span className={`text-xs px-3 py-1 rounded-full border ${getStatusColor(transaction.status)}`}>
                    {transaction.status}
                  </span>
                </div>
              </div>

              {/* Amount */}
              <div className="text-center py-4 border-y border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Transaction Amount</p>
                <p className={`text-3xl font-bold ${
                  transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {transaction.type === 'credit' ? '+' : '-'}
                  {formatCurrency(transaction.amount)}
                </p>
              </div>

              {/* Status Description */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-gray-500 mt-0.5" />
                  <p className="text-sm text-gray-700">
                    {getStatusDescription(transaction.status, metadata, transaction.description)}
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction Information */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <h3 className="font-bold text-black mb-4">Transaction Information</h3>
              <div className="space-y-3">
                <DetailItemMobile
                  label="Transaction ID"
                  value={transaction.transaction_id || transaction.id}
                  onCopy={() => copyToClipboard(transaction.transaction_id || transaction.id, 'transaction_id')}
                  copied={copiedField === 'transaction_id'}
                />
                <DetailItemMobile
                  label="Reference"
                  value={transaction.reference}
                  onCopy={() => copyToClipboard(transaction.reference, 'reference')}
                  copied={copiedField === 'reference'}
                />
                <DetailItemMobile
                  label="Created Date"
                  value={formatDateTime(transaction.created_at).date}
                  subValue={formatDateTime(transaction.created_at).time}
                />
                <DetailItemMobile
                  label="Updated Date"
                  value={formatDateTime(transaction.updated_at).date}
                  subValue={formatDateTime(transaction.updated_at).time}
                />
                {transaction.verified_at && (
                  <DetailItemMobile
                    label="Verified Date"
                    value={formatDateTime(transaction.verified_at).date}
                    subValue={formatDateTime(transaction.verified_at).time}
                  />
                )}
              </div>
            </div>

            {/* Financial Details */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <h3 className="font-bold text-black mb-4">Financial Details</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Transaction Amount</span>
                  <span className="font-medium">{formatCurrency(transaction.amount)}</span>
                </div>
                
                {parseFloat(transaction.processor_fee || 0) > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Processor Fee</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(transaction.processor_fee)}
                    </span>
                  </div>
                )}
                
                {parseFloat(transaction.platform_fee || 0) > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Platform Fee</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(transaction.platform_fee)}
                    </span>
                  </div>
                )}
                
                {parseFloat(transaction.net_amount || 0) > 0 && (
                  <div className="flex items-center justify-between py-2">
                    <span className={`font-bold ${
                      transaction.status === 'failed' ? 'text-green-600' : 'text-black'
                    }`}>
                      {formatCurrency(transaction.net_amount)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Bank Account Details */}
            {bankDetails && (
              <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
                <h3 className="font-bold text-black mb-4">Bank Account Details</h3>
                <div className="space-y-3">
                  <div className="p-3 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Bank Name</p>
                    <p className="font-medium text-black">{bankDetails.bank_name}</p>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Account Number</p>
                        <p className="font-medium text-black font-mono">{bankDetails.account_number}</p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(bankDetails.account_number, 'account_number')}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        {copiedField === 'account_number' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Account Name</p>
                    <p className="font-medium text-black">{bankDetails.account_name}</p>
                  </div>
                </div>
              </div>
            )}

            {/* OTP Status (for pending/failed OTP transactions) */}
            {metadata.otp_required && (
              <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
                <h3 className="font-bold text-black mb-4">OTP Verification</h3>
                <div className={`p-4 rounded-lg ${
                  transaction.status === 'failed' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      transaction.status === 'failed' ? 'bg-red-100' : 'bg-yellow-100'
                    }`}>
                      <Key className={`w-5 h-5 ${
                        transaction.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                      }`} />
                    </div>
                    <div>
                      <p className={`font-medium ${
                        transaction.status === 'failed' ? 'text-red-700' : 'text-yellow-700'
                      }`}>
                        {transaction.status === 'failed' ? 'OTP Verification Failed' : 'OTP Verification Required'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {transaction.status === 'failed' 
                          ? 'The OTP expired before verification was completed.'
                          : 'Please check your registered email/phone for the OTP code.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-bold text-black mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate('/wallet/transactions')}
                  className="w-full py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Back to Transactions
                </button>
                
                {transaction.status === 'failed' && (
                  <button
                    onClick={() => navigate('/wallet/withdraw')}
                    className="w-full py-3 bg-[#ff660e] text-white rounded-lg font-medium hover:bg-[#ff5500]"
                  >
                    Try Withdrawal Again
                  </button>
                )}
                
                <button
                  onClick={() => copyToClipboard(transaction.reference, 'reference_full')}
                  className="flex items-center justify-center gap-2 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  <Copy className="w-4 h-4" />
                  Copy Reference
                </button>
              </div>
              {/* Transaction Summary Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
                <h3 className="font-semibold mb-4 text-black">Transaction Summary</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {transaction.type === 'credit' ? (
                        <ArrowDownRight className="w-4 h-4 text-green-600" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-black">
                        {getTransactionTypeLabel(transaction.type, transaction.transaction_type)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {transaction.type === 'credit' ? 'Funds added to wallet' : 'Funds withdrawn from wallet'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      transaction.status === 'successful' ? 'bg-green-100' :
                      transaction.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                    }`}>
                      {getStatusIcon(transaction.status)}
                    </div>
                    <div>
                      <p className="font-medium text-black">Status: {transaction.status}</p>
                      <p className="text-sm text-gray-600">
                        {getStatusDescription(transaction.status, metadata, transaction.description)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-gray-100">
                      <Calendar className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-black">Transaction Date</p>
                      <p className="text-sm text-gray-600">{formatDateTime(transaction.created_at).date}</p>
                      <p className="text-xs text-gray-500">{formatDateTime(transaction.created_at).time}</p>
                    </div>
                  </div>
                  
                  {bankDetails && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-gray-100">
                        <Building className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-black">Bank Account</p>
                        <p className="text-sm text-gray-600">{bankDetails.bank_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{bankDetails.account_number}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Help Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold mb-4 text-black">Need Help?</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/support')}
                    className="w-full py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition text-center"
                  >
                    Contact Support
                  </button>
                  <button
                    onClick={() => navigate('/faq')}
                    className="w-full py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition text-center"
                  >
                    View FAQs
                  </button>
                  <button
                    onClick={() => navigate('/wallet/transactions')}
                    className="w-full py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition text-center"
                  >
                    View All Transactions
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/wallet/transactions')}
              className="flex items-center gap-2 text-gray-600 hover:text-black mb-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Transactions
            </button>
            <h1 className="text-2xl font-bold text-black">Transaction Details</h1>
            <p className="text-gray-600">View complete transaction information</p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {transaction && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Transaction Summary */}
            <div className="lg:col-span-2 space-y-6">
              {/* Transaction Header Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="p-4 rounded-xl bg-gray-100">
                      {getTransactionIcon(transaction.type, transaction.transaction_type)}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-black">
                        {getTransactionTypeLabel(transaction.type, transaction.transaction_type)}
                      </h2>
                      <p className="text-gray-600">
                        {transaction.description || 'No description'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusIcon(transaction.status)}
                    <span className={`px-4 py-2 rounded-full border ${getStatusColor(transaction.status)}`}>
                      {transaction.status || 'Unknown'}
                    </span>
                  </div>
                </div>

                {/* Amount Section */}
                <div className="text-center py-8 mb-6 border-y border-gray-200">
                  <p className="text-sm text-gray-600 mb-3">Transaction Amount</p>
                  <p className={`text-5xl font-bold ${
                    transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.type === 'credit' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                  </p>
                </div>

                {/* Status Description */}
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {getStatusDescription(transaction.status, metadata, transaction.description)}
                      </p>
                      {transaction.status === 'failed' && (
                        <p className="text-sm text-gray-600 mt-1">
                          Your funds remain safe in your wallet and are available for withdrawal.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Transaction Information Grid */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-black">Transaction Information</h3>
                    <div className="space-y-3">
                      <DetailItemDesktop
                        label="Transaction ID"
                        value={transaction.transaction_id || transaction.id}
                        icon={<FileText className="w-5 h-5" />}
                        onCopy={() => copyToClipboard(transaction.transaction_id || transaction.id, 'transaction_id')}
                        copied={copiedField === 'transaction_id'}
                      />
                      <DetailItemDesktop
                        label="Reference"
                        value={transaction.reference}
                        icon={<FileText className="w-5 h-5" />}
                        onCopy={() => copyToClipboard(transaction.reference, 'reference')}
                        copied={copiedField === 'reference'}
                      />
                      <DetailItemDesktop
                        label="Created Date"
                        value={formatDateTime(transaction.created_at).date}
                        subValue={formatDateTime(transaction.created_at).time}
                        icon={<Calendar className="w-5 h-5" />}
                      />
                      <DetailItemDesktop
                        label="Updated Date"
                        value={formatDateTime(transaction.updated_at).date}
                        subValue={formatDateTime(transaction.updated_at).time}
                        icon={<Clock className="w-5 h-5" />}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-black">Financial Details</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <span className="text-gray-600">Transaction Amount</span>
                        <span className="font-medium text-lg">{formatCurrency(transaction.amount)}</span>
                      </div>
                      
                      {parseFloat(transaction.processor_fee || 0) > 0 && (
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <span className="text-gray-600">Processor Fee</span>
                          <span className="font-medium text-red-600">
                            -{formatCurrency(transaction.processor_fee)}
                          </span>
                        </div>
                      )}
                      
                      {parseFloat(transaction.platform_fee || 0) > 0 && (
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <span className="text-gray-600">Platform Fee</span>
                          <span className="font-medium text-red-600">
                            -{formatCurrency(transaction.platform_fee)}
                          </span>
                        </div>
                      )}
                      
                      {parseFloat(transaction.net_amount || 0) > 0 && (
                        <div className={`flex items-center justify-between p-4 rounded-xl ${
                          transaction.status === 'failed' ? 'bg-green-50' : 'bg-gray-50'
                        }`}>
                          {/*  */}
                          <span className={`font-bold text-lg ${
                            transaction.status === 'failed' ? 'text-green-600' : 'text-black'
                          }`}>
                            {formatCurrency(transaction.net_amount)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank Account Details */}
              {bankDetails && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-black">Bank Account Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border border-gray-200 rounded-xl">
                      <p className="text-sm text-gray-600 mb-2">Bank Name</p>
                      <p className="font-semibold text-black">{bankDetails.bank_name}</p>
                      {bankDetails.bank_code && (
                        <p className="text-xs text-gray-500 mt-1">Code: {bankDetails.bank_code}</p>
                      )}
                    </div>
                    
                    <div className="p-4 border border-gray-200 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">Account Number</p>
                        <button
                          onClick={() => copyToClipboard(bankDetails.account_number, 'account_number')}
                          className="p-1 hover:bg-gray-100 rounded-lg"
                        >
                          {copiedField === 'account_number' ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                      <p className="font-semibold text-black font-mono text-lg">{bankDetails.account_number}</p>
                    </div>
                    
                    <div className="col-span-2 p-4 border border-gray-200 rounded-xl">
                      <p className="text-sm text-gray-600 mb-2">Account Name</p>
                      <p className="font-semibold text-black">{bankDetails.account_name}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* OTP Status */}
              {metadata.otp_required && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-black">OTP Verification Status</h3>
                  <div className={`p-6 rounded-xl ${
                    transaction.status === 'failed' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
                  }`}>
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-full ${
                        transaction.status === 'failed' ? 'bg-red-100' : 'bg-yellow-100'
                      }`}>
                        <Key className={`w-6 h-6 ${
                          transaction.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <h4 className={`text-lg font-semibold ${
                          transaction.status === 'failed' ? 'text-red-700' : 'text-yellow-700'
                        }`}>
                          {transaction.status === 'failed' ? 'OTP Verification Failed' : 'OTP Verification Required'}
                        </h4>
                        <p className="text-gray-700 mt-2">
                          {transaction.status === 'failed' 
                            ? 'The One-Time Password (OTP) expired before it could be verified. This is a security measure to protect your funds.'
                            : 'A One-Time Password (OTP) has been sent to your registered contact method. Please enter the OTP to complete the withdrawal.'}
                        </p>
                        {transaction.status === 'failed' && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-gray-700">What happened:</p>
                            <ul className="mt-2 space-y-1 text-sm text-gray-600">
                              <li className="flex items-start gap-2">
                                <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                <span>The OTP was generated but not verified within the time limit</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <ShieldIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                <span>Your funds are secure and remain in your wallet</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <RefreshCw className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>You can attempt the withdrawal again with a new OTP</span>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Actions & Summary */}
            <div className="space-y-6">
              {/* Actions Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-black">Actions</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/wallet/transactions')}
                    className="w-full py-3 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition text-center"
                  >
                    Back to Transactions
                  </button>
                  
                  {transaction.status === 'failed' && (
                    <>
                      <button
                        onClick={() => navigate('/wallet/withdraw')}
                        className="w-full py-3 bg-[#ff660e] text-white rounded-xl font-semibold hover:bg-[#ff5500] transition text-center"
                      >
                        Try Withdrawal Again
                      </button>
                      
                      <button
                        onClick={() => navigate('/wallet')}
                        className="w-full py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition text-center"
                      >
                        Go to Wallet
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={() => copyToClipboard(transaction.reference, 'reference_full')}
                    className="w-full flex items-center justify-center gap-3 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition"
                  >
                    <Copy className="w-5 h-5" />
                    Copy Reference
                  </button>
                  
                  <button
                    onClick={() => copyToClipboard(transaction.transaction_id || transaction.id, 'id_full')}
                    className="w-full flex items-center justify-center gap-3 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition"
                  >
                    <Copy className="w-5 h-5" />
                    Copy Transaction ID
                  </button>
                </div>
              </div>

              {/* Transaction Summary Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
                <h3 className="font-semibold mb-4 text-black">Transaction Summary</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {transaction.type === 'credit' ? (
                        <ArrowDownRight className="w-4 h-4 text-green-600" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-black">
                        {getTransactionTypeLabel(transaction.type, transaction.transaction_type)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {transaction.type === 'credit' ? 'Funds added to wallet' : 'Funds withdrawn from wallet'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      transaction.status === 'successful' ? 'bg-green-100' :
                      transaction.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                    }`}>
                      {getStatusIcon(transaction.status)}
                    </div>
                    <div>
                      <p className="font-medium text-black">Status: {transaction.status}</p>
                      <p className="text-sm text-gray-600">
                        {getStatusDescription(transaction.status, metadata, transaction.description)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-gray-100">
                      <Calendar className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-black">Transaction Date</p>
                      <p className="text-sm text-gray-600">{formatDateTime(transaction.created_at).date}</p>
                      <p className="text-xs text-gray-500">{formatDateTime(transaction.created_at).time}</p>
                    </div>
                  </div>
                  
                  {bankDetails && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-gray-100">
                        <Building className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-black">Bank Account</p>
                        <p className="text-sm text-gray-600">{bankDetails.bank_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{bankDetails.account_number}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Help Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold mb-4 text-black">Need Help?</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/support')}
                    className="w-full py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition text-center"
                  >
                    Contact Support
                  </button>
                  <button
                    onClick={() => navigate('/faq')}
                    className="w-full py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition text-center"
                  >
                    View FAQs
                  </button>
                  <button
                    onClick={() => navigate('/wallet/transactions')}
                    className="w-full py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition text-center"
                  >
                    View All Transactions
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
    </>
  );
};

// Mobile Detail Item Component
const DetailItemMobile = ({ label, value, subValue, onCopy, copied }) => (
  <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
    <div>
      <p className="text-sm text-gray-600">{label}</p>
      <p className="font-medium text-black truncate">{value || 'N/A'}</p>
      {subValue && <p className="text-xs text-gray-500">{subValue}</p>}
    </div>
    {onCopy && value && (
      <button
        onClick={onCopy}
        className="p-2 rounded-lg hover:bg-gray-100"
        title="Copy to clipboard"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-500" />
        ) : (
          <Copy className="w-4 h-4 text-gray-500" />
        )}
      </button>
    )}
  </div>
);

// Desktop Detail Item Component
const DetailItemDesktop = ({ label, value, subValue, icon, onCopy, copied }) => (
  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
    <div className="flex items-center gap-3">
      <div className="text-gray-500">{icon}</div>
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        <p className="font-medium text-black">{value || 'N/A'}</p>
        {subValue && <p className="text-sm text-gray-500">{subValue}</p>}
      </div>
    </div>
    {onCopy && value && (
      <button
        onClick={onCopy}
        className="p-2 rounded-lg hover:bg-gray-100 transition"
        title="Copy to clipboard"
      >
        {copied ? (
          <Check className="w-5 h-5 text-green-500" />
        ) : (
          <Copy className="w-5 h-5 text-gray-500" />
        )}
      </button>
    )}
  </div>
);

export default TransactionDetails;